import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import { Button, Card, CardContent, TableSkeleton } from '@/components/ui';
import { ApiErrorDisplay } from '@/components/features/error';
import { ServiceCard, ServiceForm, DeleteConfirmation } from '@/components/features/services';
import { useServices, useCreateService, useUpdateService, useDeleteService } from '@/hooks/use-services';
import type { Service, CreateServiceRequest, UpdateServiceRequest } from '@/types/service';

/**
 * Services list page.
 * Route: /services
 *
 * Features:
 * - List all services in a card grid
 * - Create new service via dialog
 * - Edit existing service via dialog
 * - Delete (deactivate) service with confirmation
 */
export function ServicesPage() {
  const { t } = useTranslation('services');

  // Data fetching
  const { data, isLoading, error, refetch } = useServices();

  // Mutations
  const createMutation = useCreateService();
  const updateMutation = useUpdateService();
  const deleteMutation = useDeleteService();

  // Dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingService, setDeletingService] = useState<Service | null>(null);

  // Handlers
  const handleCreateClick = () => {
    setEditingService(null);
    setFormOpen(true);
  };

  const handleEditClick = (service: Service) => {
    setEditingService(service);
    setFormOpen(true);
  };

  const handleDeleteClick = (service: Service) => {
    setDeletingService(service);
    setDeleteDialogOpen(true);
  };

  const handleCreate = (data: CreateServiceRequest) => {
    createMutation.mutate(data, {
      onSuccess: () => {
        setFormOpen(false);
      },
    });
  };

  const handleUpdate = (serviceId: string, data: UpdateServiceRequest) => {
    updateMutation.mutate(
      { serviceId, data },
      {
        onSuccess: () => {
          setFormOpen(false);
          setEditingService(null);
        },
      }
    );
  };

  const handleDeleteConfirm = () => {
    if (!deletingService) return;
    deleteMutation.mutate(deletingService.serviceId, {
      onSuccess: () => {
        setDeleteDialogOpen(false);
        setDeletingService(null);
      },
    });
  };

  return (
    <div>
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('common:button.refresh')}
            </Button>
            <Button size="sm" onClick={handleCreateClick}>
              <Plus className="mr-2 h-4 w-4" />
              {t('list.createNew')}
            </Button>
          </div>
        }
      />

      {/* Loading state */}
      {isLoading && (
        <Card>
          <CardContent className="py-6">
            <TableSkeleton rows={3} cols={4} />
          </CardContent>
        </Card>
      )}

      {/* Error state */}
      {error && <ApiErrorDisplay error={error} onRetry={() => refetch()} />}

      {/* Empty state */}
      {!isLoading && !error && data?.data.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">{t('list.empty')}</p>
            <Button className="mt-4" onClick={handleCreateClick}>
              <Plus className="mr-2 h-4 w-4" />
              {t('list.createFirst')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Service grid */}
      {!isLoading && !error && data && data.data.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {data.data.map((service) => (
            <ServiceCard
              key={service.serviceId}
              service={service}
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
            />
          ))}
        </div>
      )}

      {/* Pagination hint */}
      {data?.pagination.hasMore && (
        <div className="mt-4 text-center">
          <p className="text-sm text-muted-foreground">
            {t('list.moreAvailable')}
          </p>
        </div>
      )}

      {/* Create/Edit Form Dialog */}
      <ServiceForm
        service={editingService}
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditingService(null);
        }}
        onSubmitCreate={handleCreate}
        onSubmitUpdate={handleUpdate}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmation
        service={deletingService}
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);
          if (!open) setDeletingService(null);
        }}
        onConfirm={handleDeleteConfirm}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  );
}
